import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'

import { AppLanguage } from '~/types/locale'

import { RootState } from '~/redux/store'
import {
  MathVaultScreen,
  Alternative,
  AnswerState,
  LevelSummaryModel,
  VaultSpawnLocations,
  MathVaultState,
  QuestionSummaryModel,
  activityBeforeLogIn,
  MathVaultLocation,
  MathVaultActivity,
  ColumnLayoutType,
  LevelActivity,
  MathVaultActivityMetadata,
  VaultColors,
} from '~/types/math-vault'

const ACTIVITY_METADATA = {
  language: AppLanguage.EN,
  chest: '',
  tab: 1,
  elevator: '',
  room: '',
  metaDescription: '',
  keywords: '',
  globalId: '',
  activityNumber: 1,
  roomColor: VaultColors.PURPLE,
  numberOfTabs: 6,
  numberOfActivities: 10,
  reference: '',
  references: [],
  title: '',
  points: 0,
  tabsActivity: [],
  columnLayout: ColumnLayoutType.ONE_COLUMN,
}

const ACTIVITY = {
  _id: '',
  metadata: ACTIVITY_METADATA,
  question: [],
  alternatives: [],
  shouldShuffle: false,
}

const INITIAL_LIVES = 2
const INITIAL_SUMMARY = {
  livesLeft: INITIAL_LIVES,
  questionNo: null,
  numberOfTries: -1,
  numberOfFailures: -1,
  firstPassOnTryNo: -1,
  passed: false,
}
const INITIAL_ACTIVITY_BEFORE_LOGIN = {
  metadata: null,
  nextActivityPath: null,
}

const initialState: MathVaultState = {
  currentScreen: MathVaultScreen.ACTIVITY,
  answerState: AnswerState.PENDING,
  levelsSummary: {},
  currentQuestionSummary: INITIAL_SUMMARY,
  selectedAnswer: null,
  currentLocation: {
    room: VaultSpawnLocations.outside_vault,
    chest: null,
  },
  kudosEarned: 0,
  answerBeforeLogIn: INITIAL_ACTIVITY_BEFORE_LOGIN,
  server: {
    activity: ACTIVITY,
  },
  loading: false,
  streak: -1,
  navigations: {
    prevLevel: 1,
    prevActivity: 1,
    nextLevel: 1,
    nextActivity: 1,
  },
  pickedEgg: false,
  pickedTempEgg: false,
}

const slice = createSlice({
  name: 'mathVault',
  initialState,
  reducers: {
    setActivity: (state, action: PayloadAction<MathVaultActivity>) => ({
      ...state,
      activity: action.payload,
    }),
    decrementLivesLeft: ({ currentQuestionSummary, ...state }) => ({
      ...state,
      currentQuestionSummary: {
        ...currentQuestionSummary,
        livesLeft: currentQuestionSummary.livesLeft - 1,
      },
    }),

    resetAnswerState: (state) => ({
      ...state,
      answerState: AnswerState.PENDING,
    }),

    resetLivesLeft: ({ currentQuestionSummary, ...state }) => ({
      ...state,
      currentQuestionSummary: {
        ...currentQuestionSummary,
        livesLeft: INITIAL_LIVES,
      },
    }),

    resetSelectedAnswer: (state) => ({
      ...state,
      selectedAnswer: null,
    }),

    resetQuestionSummary: (state) => ({
      ...state,
      currentQuestionSummary: INITIAL_SUMMARY,
    }),

    resetLevelsSummary: (state) => ({
      ...state,
      levelsSummary: {},
    }),

    setCurrentScreen: (state, action: PayloadAction<MathVaultScreen>) => ({
      ...state,
      currentScreen: action.payload,
    }),

    setAnswerState: (state, action: PayloadAction<AnswerState.CORRECT | AnswerState.INCORRECT>) => ({
      ...state,
      answerState: action.payload,
    }),

    setCurrentLocation: (state, action: PayloadAction<MathVaultLocation>) => ({
      ...state,
      currentLocation: action.payload,
    }),

    setSelectedAnswer: (state, action: PayloadAction<Alternative>) => ({
      ...state,
      selectedAnswer: action.payload,
    }),

    setCurrentQuestionSummary: (state, action: PayloadAction<QuestionSummaryModel>) => ({
      ...state,
      currentQuestionSummary: action.payload,
    }),

    setLevelsSummary: (state, action: PayloadAction<{ [key: number]: LevelSummaryModel }>) => ({
      ...state,
      levelsSummary: action.payload,
    }),

    setKudosEarned: (state, action: PayloadAction<number>) => ({
      ...state,
      kudosEarned: action.payload,
    }),

    setStreak: (state, action: PayloadAction<number>) => ({
      ...state,
      streak: action.payload,
    }),

    setLivesLeft: ({ currentQuestionSummary, ...state }, action: PayloadAction<number>) => ({
      ...state,
      currentQuestionSummary: {
        ...currentQuestionSummary,
        livesLeft: action.payload,
      },
    }),

    setAnswerBeforeLogIn: (state, action: PayloadAction<activityBeforeLogIn>) => ({
      ...state,
      answerBeforeLogIn: action.payload,
    }),

    resetAnswerBeforeLogIn: (state) => ({
      ...state,
      answerBeforeLogIn: INITIAL_ACTIVITY_BEFORE_LOGIN,
    }),

    setLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      loading: action.payload,
    }),

    resetState: () => initialState,

    setPrevLevel: (state, action: PayloadAction<number>) => ({
      ...state,
      navigations: {
        ...state.navigations,
        prevLevel: action.payload,
      },
    }),

    setPrevActivity: (state, action: PayloadAction<number>) => ({
      ...state,
      navigations: {
        ...state.navigations,
        prevActivity: action.payload,
      },
    }),

    setNextLevel: (state, action: PayloadAction<number>) => ({
      ...state,
      navigations: {
        ...state.navigations,
        nextLevel: action.payload,
      },
    }),

    setNextActivity: (state, action: PayloadAction<number>) => ({
      ...state,
      navigations: {
        ...state.navigations,
        nextActivity: action.payload,
      },
    }),
    setPickedEgg: (state, action: PayloadAction<boolean>) => ({
      ...state,
      pickedEgg: action.payload,
    }),
    setPickedTempEgg: (state, action: PayloadAction<boolean>) => ({
      ...state,
      pickedTempEgg: action.payload,
    }),
  },

  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action) => {
      return {
        ...state,
        server: { activity: action.payload.mathVault.activity },
      }
    })
  },
})

export const selectActivity = (state: RootState): MathVaultActivity => state.mathVault.server.activity

export const selectCurrentScreen = (state: RootState): MathVaultState['currentScreen'] => {
  return state.mathVault.currentScreen
}

export const selectCurrentQuestionSummary = (state: RootState): MathVaultState['currentQuestionSummary'] => {
  return state.mathVault.currentQuestionSummary
}

export const selectAttemptNumber = (state: RootState): number => {
  return state.mathVault.currentQuestionSummary.numberOfTries + 1
}

export const selectDidAnswer = (state: RootState): boolean => {
  return state.mathVault.answerState !== AnswerState.PENDING
}

export const selectDidAnswerCorrectly = (state: RootState): boolean => {
  return state.mathVault.answerState === AnswerState.CORRECT
}

export const selectDidAnswerIncorrectly = (state: RootState): boolean => {
  return state.mathVault.answerState === AnswerState.INCORRECT
}

export const selectDidSelectAnAnswer = (state: RootState): boolean => {
  return Boolean(state.mathVault.selectedAnswer)
}

export const selectIsActivityScreen = (state: RootState): boolean => {
  return state.mathVault.currentScreen === MathVaultScreen.ACTIVITY
}

export const selectIsFailureScreen = (state: RootState): boolean => {
  return state.mathVault.currentScreen === MathVaultScreen.FAILURE
}

export const selectIsCompleteLevelScreen = (state: RootState): boolean => {
  return state.mathVault.currentScreen === MathVaultScreen.COMPLETE_LEVEL
}

export const selectIsCompleteChestScreen = (state: RootState): boolean => {
  return state.mathVault.currentScreen === MathVaultScreen.COMPLETE_CHEST
}

export const selectIsOnLastLife = (state: RootState): boolean => {
  return state.mathVault.currentQuestionSummary.livesLeft === 1
}

export const selectIsOutOfLives = (state: RootState): boolean => {
  return state.mathVault.currentQuestionSummary.livesLeft === 0
}

export const selectLivesInitialCount = (): number => {
  return INITIAL_LIVES
}

export const selectLivesLeft = (state: RootState): MathVaultState['currentQuestionSummary']['livesLeft'] => {
  return state.mathVault.currentQuestionSummary.livesLeft
}

export const selectSelectedAnswer = (state: RootState): MathVaultState['selectedAnswer'] => {
  return state.mathVault.selectedAnswer
}

export const selectMathVaultLocation = (state: RootState): MathVaultState['currentLocation'] => {
  return state.mathVault.currentLocation
}

export const selectLevelsSummary = (state: RootState): MathVaultState['levelsSummary'] => {
  return state.mathVault.levelsSummary
}

export const selectKudosEarned = (state: RootState): MathVaultState['kudosEarned'] => {
  return state.mathVault.kudosEarned
}

export const selectStreak = (state: RootState): MathVaultState['streak'] => {
  return state.mathVault.streak
}

export const selectIsQuestionPassed = (state: RootState): boolean => {
  return state.mathVault.currentQuestionSummary.passed
}

export const selectAnswerBeforeLogIn = (state: RootState): activityBeforeLogIn => {
  return state.mathVault.answerBeforeLogIn
}

export const selectLoading = (state: RootState): boolean => {
  return state.mathVault.loading
}

export const selectRoomColor = (state: RootState): VaultColors => {
  return state.mathVault.server.activity.metadata.roomColor
}

export const selectActivityTitle = (state: RootState): string => {
  return state.mathVault.server.activity.metadata.title
}

export const selectActivityNumber = (state: RootState): number => {
  return state.mathVault.server.activity.metadata.activityNumber
}

export const selectCurrentLevel = (state: RootState): number => {
  return state.mathVault.server.activity.metadata.tab
}

export const selectTabsActivity = (state: RootState): LevelActivity[] => {
  return state.mathVault.server.activity.metadata.tabsActivity
}

export const selectNumberOfActivities = (state: RootState): number => {
  return state.mathVault.server.activity.metadata.numberOfActivities
}

export const selectNumberOfTabs = (state: RootState): number => {
  return state.mathVault.server.activity.metadata.numberOfTabs
}

export const selectQuestion = (state: RootState): unknown[] => {
  return state.mathVault.server.activity.question
}

export const selectColumnLayout = (state: RootState): ColumnLayoutType => {
  return state.mathVault.server.activity.metadata.columnLayout
}

export const selectAlternatives = (state: RootState): Alternative[] => {
  return state.mathVault.server.activity.alternatives
}

export const selectActivityMetadata = (state: RootState): MathVaultActivityMetadata => {
  return state.mathVault.server.activity.metadata
}

export const selectPrevLevel = (state: RootState): number => {
  return state.mathVault.navigations.prevLevel
}

export const selectPrevActivity = (state: RootState): number => {
  return state.mathVault.navigations.prevActivity
}

export const selectNextLevel = (state: RootState): number => {
  return state.mathVault.navigations.nextLevel
}

export const selectNextActivity = (state: RootState): number => {
  return state.mathVault.navigations.nextActivity
}

export const selectPickedEgg = (state: RootState): boolean => {
  return state.mathVault.pickedEgg
}
export const selectPickedTempEgg = (state: RootState): boolean => {
  return state.mathVault.pickedTempEgg
}

export const {
  setActivity: setActivityActionCreator,
  decrementLivesLeft: decrementLivesLeftActionCreator,
  resetAnswerState: resetAnswerStateActionCreator,
  resetLivesLeft: resetLivesLeftActionCreator,
  resetSelectedAnswer: resetSelectedAnswerActionCreator,
  resetLevelsSummary: resetLevelsSummaryActionCreator,
  setCurrentScreen: setCurrentScreenActionCreator,
  setSelectedAnswer: setSelectedAnswerActionCreator,
  setAnswerState: setAnswerStateActionCreator,
  setCurrentLocation: setCurrentLocationCreator,
  setLevelsSummary: setLevelsSummaryActionCreator,
  setCurrentQuestionSummary: setCurrentQuestionSummaryActionCreator,
  setKudosEarned: setKudosEarnedActionCreator,
  setStreak: setStreakActionCreator,
  setLivesLeft: setLivesLeftActionCreator,
  resetQuestionSummary: resetQuestionSummaryActionCreator,
  setAnswerBeforeLogIn: setAnswerBeforeLogIn,
  resetAnswerBeforeLogIn: resetAnswerBeforeLogIn,
  resetState: resetState,
  setLoading: setLoadingActionCreator,
  setPrevLevel: setPrevLevelActionCreator,
  setPrevActivity: setPrevActivityActionCreator,
  setNextLevel: setNextLevelActionCreator,
  setNextActivity: setNextActivityActionCreator,
  setPickedEgg: setPickedEggActionCreator,
  setPickedTempEgg: setPickedTempEggActionCreator,
} = slice.actions

export default slice.reducer
